import { Injectable, inject } from '@angular/core';
import { LeafletMouseEvent } from 'leaflet';
import { GlobalsStore } from '../../stores/globals.state';
import { Indicator } from '../../models/indicator';
import { DataEntry } from '../../models/indicator-data';

export interface FeatureInfo {
  feature: any;
  value: number | null;
  indicator: Indicator | null;
}

@Injectable({
  providedIn: 'root',
})
export class MapEventService {
  private readonly globalsStore = inject(GlobalsStore);

  /**
   * Highlights a feature on the map based on the provided event and data, and triggers a highlight event.
   *
   * @param {LeafletMouseEvent} e - The mouse event that triggered the feature highlighting.
   * @param {string} widgetId - The ID of the widget where the feature is being highlighted.
   * @param {Indicator | null} indicator - The indicator associated with the feature, or null if none.
   * @param {DataEntry | null} data - The data entry related to the feature, or null if not applicable.
   * @param {string | null} currentHighlight - The key of the currently highlighted feature, or null if none.
   * @param {function(FeatureInfo): void} onHighlight - Callback function to handle the new feature information after highlighting.
   * @return {void} This method does not return a value.
   */
  highlightFeature(
    e: LeafletMouseEvent,
    widgetId: string,
    indicator: Indicator | null,
    data: DataEntry | null,
    currentHighlight: string | null,
    onHighlight: (featureInfo: FeatureInfo) => void
  ): void {
    const feature: any = e.target.feature;
    const featureKey = feature.properties.key || feature.id;

    if (featureKey === currentHighlight) return;

    const newFeatureInfo: FeatureInfo = {
      feature: feature,
      indicator: indicator || null,
      value: data ? data[featureKey] : null,
    };

    onHighlight(newFeatureInfo);

    this.globalsStore.triggerAtlasEvent('highlightDistrict', {
      widgetId: widgetId,
      key: String(featureKey),
      type: 'feature',
    });
  }

  /**
   * Resets the current highlight for a specific widget and triggers an event to update the highlight state.
   *
   * @param {string} widgetId - The unique identifier of the widget for which the highlight should be reset.
   * @param {string | null} currentHighlight - The current highlight value. If null, no reset is performed.
   * @param {Function} onReset - A callback function invoked when the highlight is reset.
   * @return {void} This method does not return a value.
   */
  resetHighlight(
    widgetId: string,
    currentHighlight: string | null,
    onReset: () => void
  ): void {
    if (!currentHighlight) return;

    onReset();

    this.globalsStore.triggerAtlasEvent('highlightDistrict', {
      widgetId: widgetId,
      key: undefined,
    });
  }

  /**
   * Handles the selection of a district feature on a map and triggers an event to mark the district.
   *
   * @param {object} e - The event object containing details about the interaction and feature properties.
   * @return {void} This method does not return a value.
   */
  selectDistrictAndZoomToFeature(e: any): void {
    const key = e.sourceTarget.feature.properties.key;
    this.globalsStore.triggerAtlasEvent('markDistrict', { key });
  }
}
